import React, { useMemo, useState } from 'react'
import { Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Table } from '@surecompanies/core_components'
import { InvoiceCensusRecordDto } from '../../../api/dto/generated-invoice.dto'
import {
  CensusCaption,
  CensusSummary,
  StatCard,
  StatHint,
  StatLabel,
  StatValue,
} from './CensusTable.styles'

const censusName = (record: InvoiceCensusRecordDto): string => {
  const name = [record.firstName, record.lastName].filter(Boolean).join(' ')
  return name || record.employeePublicKey || '—'
}

interface BenefitWindow {
  effective: string | null
  end: string | null
}

const isEnrolled = (
  record: InvoiceCensusRecordDto,
  enrolledKeys?: Set<string>
): boolean =>
  Boolean(
    record.employeePublicKey && enrolledKeys?.has(record.employeePublicKey)
  )

const benefitWindowFor = (
  record: InvoiceCensusRecordDto,
  enrolledBenefitWindows?: Map<string, BenefitWindow>
): BenefitWindow | undefined =>
  record.employeePublicKey
    ? enrolledBenefitWindows?.get(record.employeePublicKey)
    : undefined

const buildCensusColumns = (
  enrolledKeys?: Set<string>,
  enrolledBenefitWindows?: Map<string, BenefitWindow>
): ColumnsType<InvoiceCensusRecordDto> => {
  const columns: ColumnsType<InvoiceCensusRecordDto> = [
    {
      title: 'EMPLOYEE',
      key: 'name',
      render: (_, record) => censusName(record),
    },
    {
      title: 'DEPARTMENT',
      key: 'department',
      render: (_, record) => record.department || '—',
    },
    {
      title: 'HIRE',
      key: 'hireDate',
      render: (_, record) => record.hireDate ?? '—',
    },
    {
      title: 'TERMINATION',
      key: 'terminationDate',
      render: (_, record) => record.terminationDate ?? '—',
    },
    {
      title: 'ELIGIBLE IN MONTH',
      key: 'employedInMonth',
      align: 'center',
      render: (_, record) =>
        record.employedInMonth ? (
          <Tag color="green">Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    },
  ]

  if (enrolledKeys) {
    columns.push({
      title: 'ENROLLED',
      key: 'enrolled',
      align: 'center',
      render: (_, record) =>
        isEnrolled(record, enrolledKeys) ? (
          <Tag color="blue">Yes</Tag>
        ) : (
          <Tag color="default">No</Tag>
        ),
    })
  }

  if (enrolledBenefitWindows) {
    columns.push(
      {
        title: 'BENEFIT EFFECTIVE',
        key: 'benefitEffective',
        render: (_, record) =>
          benefitWindowFor(record, enrolledBenefitWindows)?.effective ?? '—',
      },
      {
        title: 'BENEFIT END',
        key: 'benefitEnd',
        render: (_, record) =>
          benefitWindowFor(record, enrolledBenefitWindows)?.end ?? '—',
      }
    )
  }

  return columns
}

interface CensusTableProps {
  records: InvoiceCensusRecordDto[]
  /**
   * Employee public keys enrolled in the funded month (have a premium plan).
   * Derived from the invoice's funded-month line items; lets the census flag and
   * filter enrolled members. Count matches the Calculated Fees "Enrolled" figure.
   */
  enrolledKeys?: Set<string>
  /**
   * Per-enrolled-employee benefit window (effective/end) from the funded-month
   * Health Plans rows. When supplied, the table renders Benefit Effective / End
   * columns — the dates that drive the enrolled flag.
   */
  enrolledBenefitWindows?: Map<string, BenefitWindow>
  /** Vertical scroll height for the table body. */
  scrollY?: number
}

// Which census subset the table is showing. Driven by the clickable stat cards.
type CensusFilter = 'all' | 'enrolled' | 'inMonth' | 'notInMonth'

// Shared eligible-employee census table. The generated-invoice drawer feeds it
// the census captured on the invoice (deterministic snapshot).
const CensusTable: React.FC<CensusTableProps> = ({
  records,
  enrolledKeys,
  enrolledBenefitWindows,
  scrollY = 360,
}) => {
  const poolCount = records.length
  const eligibleInMonth = records.filter((row) => row.employedInMonth).length
  const excluded = poolCount - eligibleInMonth

  // Enrolled = census members with a premium plan in the funded month. Only show
  // the card/column/filter when the caller supplies enrollment keys.
  const showEnrolled = Boolean(enrolledKeys)
  const enrolledCount = useMemo(
    () =>
      enrolledKeys
        ? records.filter((row) => isEnrolled(row, enrolledKeys)).length
        : 0,
    [records, enrolledKeys]
  )

  const [filter, setFilter] = useState<CensusFilter>('all')

  // Clicking the active card again clears back to the full pool, so the cards
  // double as a toggle.
  const handleSelect = (next: CensusFilter): void =>
    setFilter((curr) => (curr === next ? 'all' : next))

  const columns = useMemo(
    () => buildCensusColumns(enrolledKeys, enrolledBenefitWindows),
    [enrolledKeys, enrolledBenefitWindows]
  )

  const visibleRecords = useMemo(() => {
    if (filter === 'enrolled')
      return records.filter((row) => isEnrolled(row, enrolledKeys))
    if (filter === 'inMonth')
      return records.filter((row) => row.employedInMonth)
    if (filter === 'notInMonth')
      return records.filter((row) => !row.employedInMonth)
    return records
  }, [records, filter, enrolledKeys])

  return (
    <>
      <CensusSummary>
        <StatCard
          type="button"
          aria-pressed={filter === 'all'}
          $active={filter === 'all'}
          onClick={() => setFilter('all')}
        >
          <StatValue>{poolCount}</StatValue>
          <StatLabel>In pool</StatLabel>
          <StatHint>benefits-eligible &amp; active</StatHint>
        </StatCard>
        {showEnrolled ? (
          <StatCard
            type="button"
            aria-pressed={filter === 'enrolled'}
            $active={filter === 'enrolled'}
            onClick={() => handleSelect('enrolled')}
          >
            <StatValue $accent>{enrolledCount}</StatValue>
            <StatLabel>Enrolled</StatLabel>
            <StatHint>active plan in the funded month</StatHint>
          </StatCard>
        ) : null}
        <StatCard
          type="button"
          aria-pressed={filter === 'inMonth'}
          $active={filter === 'inMonth'}
          onClick={() => handleSelect('inMonth')}
        >
          <StatValue $accent>{eligibleInMonth}</StatValue>
          <StatLabel>Eligible in month</StatLabel>
          <StatHint>employed during the funded month</StatHint>
        </StatCard>
        <StatCard
          type="button"
          aria-pressed={filter === 'notInMonth'}
          $active={filter === 'notInMonth'}
          onClick={() => handleSelect('notInMonth')}
        >
          <StatValue>{excluded}</StatValue>
          <StatLabel>Not in month</StatLabel>
          <StatHint>hired/termed outside the month</StatHint>
        </StatCard>
      </CensusSummary>
      <CensusCaption>
        Pool = employees flagged benefits-eligible and active. Only the{' '}
        {eligibleInMonth} employed during the funded month count toward billing.
        {showEnrolled ? ` ${enrolledCount} enrolled in a plan.` : ''}
        {filter !== 'all'
          ? ` Showing ${visibleRecords.length} of ${poolCount} — tap the highlighted card to clear.`
          : ' Tap a card to filter the list.'}
      </CensusCaption>
      <Table
        dataSource={visibleRecords}
        columns={columns}
        rowKey={(row: InvoiceCensusRecordDto, index?: number) =>
          row.employeePublicKey ?? row.employeeId ?? String(index)
        }
        pagination={false}
        size="small"
        scroll={{ y: scrollY }}
      />
    </>
  )
}

export { CensusTable }
